import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getPostBySlug } from '../data/blogPosts';
import './LandingPage.css';
import './BlogPage.css';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const formatDateISO = (humanDate) => {
  const match = /^(\w+)\s+(\d+),\s+(\d{4})$/.exec(humanDate || '');
  if (!match) return humanDate;
  const monthIndex = MONTHS.indexOf(match[1]);
  if (monthIndex === -1) return humanDate;
  const mm = String(monthIndex + 1).padStart(2, '0');
  const dd = String(match[2]).padStart(2, '0');
  return `${match[3]}-${mm}-${dd}`;
};

const SharedRootsLogo = ({ width = 28, height = 30 }) => {
  return (
    <svg width={width} height={height} viewBox="0 0 160 160" fill="none">
      <path d="M80 140 L80 95" stroke="#1B6B4A" strokeWidth="7" strokeLinecap="round"/>
      <path d="M80 95 Q65 80, 50 65" stroke="#1B6B4A" strokeWidth="6" strokeLinecap="round" fill="none"/>
      <path d="M80 95 Q95 80, 110 65" stroke="#1B6B4A" strokeWidth="6" strokeLinecap="round" fill="none"/>
      <circle cx="44" cy="50" r="25" fill="#F5C842" opacity="0.85"/>
      <circle cx="116" cy="50" r="25" fill="#F2886D" opacity="0.85"/>
      <circle cx="80" cy="92" r="10" fill="#3BA676"/>
    </svg>
  );
};

const BlogNav = ({ navigate, mobileNavOpen, setMobileNavOpen, navScrolled }) => (
  <nav className={`lp-nav${navScrolled ? ' scrolled' : ''}`}>
    <div className="lp-nav-inner">
      <div className="lp-nav-logo" onClick={() => navigate('/')}>
        <SharedRootsLogo width={38} height={40} />
        <span className="lp-nav-wordmark">coparent</span>
      </div>
      <ul className={`lp-nav-links${mobileNavOpen ? ' mobile-open' : ''}`}>
        <li><button className="lp-nav-link-btn" onClick={() => navigate('/')}>Home</button></li>
        <li><button className="lp-nav-link-btn" onClick={() => { setMobileNavOpen(false); navigate('/blog'); }}>Blog</button></li>
        <li><button className="lp-nav-login" onClick={() => navigate('/login')}>Log In</button></li>
        <li><button className="lp-nav-cta" onClick={() => navigate('/register')}>Get Started</button></li>
      </ul>
      <button className="lp-nav-mobile-toggle" onClick={() => setMobileNavOpen(!mobileNavOpen)} aria-label="Toggle menu">
        <span></span>
        <span></span>
        <span></span>
      </button>
    </div>
  </nav>
);

const renderBlock = (block, index) => {
  switch (block.type) {
    case 'lead':
      return <p key={index} className="post-lead" dangerouslySetInnerHTML={{ __html: block.text }} />;

    case 'quick-answer':
      return (
        <aside key={index} className="post-quick-answer" aria-label="Quick answer">
          <div className="post-quick-answer-label">Quick answer</div>
          <ul>
            {block.items.map((item, i) => (
              <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
            ))}
          </ul>
        </aside>
      );

    case 'paragraph':
      return <p key={index} dangerouslySetInnerHTML={{ __html: block.text }} />;

    case 'heading':
      return <h2 key={index}>{block.text}</h2>;

    case 'list':
      return (
        <ul key={index}>
          {block.items.map((item, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
          ))}
        </ul>
      );

    case 'pattern-card':
      return (
        <div key={index} className="post-pattern-card">
          <h3>{block.title}</h3>
          <p dangerouslySetInnerHTML={{ __html: block.text }} />
        </div>
      );

    case 'question-list':
      return (
        <ul key={index} className="post-question-list">
          {block.items.map((item, i) => (
            <li key={i}><strong>{item.bold}</strong> <span dangerouslySetInnerHTML={{ __html: item.text }} /></li>
          ))}
        </ul>
      );

    case 'category-grid':
      return (
        <ul key={index} className="post-category-grid">
          {block.items.map((item, i) => (
            <li key={i}>
              <span className={`post-cat-icon ${item.colorClass}`}>{item.icon}</span>
              {item.label}
            </li>
          ))}
        </ul>
      );

    case 'split-options':
      return (
        <div key={index} className="post-split-options">
          {block.items.map((item, i) => (
            <div key={i} className="post-split-option">
              <div className="post-split-ratio">{item.ratio}</div>
              <div className="post-split-label">{item.label}</div>
              <p className="post-split-desc">{item.description}</p>
            </div>
          ))}
        </div>
      );

    case 'tip-card':
      return (
        <div key={index} className="post-tip-card">
          <h3>{block.title}</h3>
          <p dangerouslySetInnerHTML={{ __html: block.text }} />
        </div>
      );

    case 'principle':
      return (
        <div key={index} className="post-principle">
          <strong>{block.bold}</strong>
          <p dangerouslySetInnerHTML={{ __html: block.text }} />
        </div>
      );

    case 'cta': {
      const ctaHref = block.href || '/register';
      const isExternal = /^https?:\/\//.test(ctaHref);
      return (
        <div key={index} className="post-cta-block">
          <p>{block.text}</p>
          <a
            href={ctaHref}
            {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          >
            {block.buttonText}
          </a>
        </div>
      );
    }

    default:
      return null;
  }
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getPostBySlug(slug);
  const [navScrolled, setNavScrolled] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    const handleScroll = () => {
      setNavScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!post) {
    return (
      <div className="landing-page blog-post-page">
        <BlogNav navigate={navigate} mobileNavOpen={mobileNavOpen} setMobileNavOpen={setMobileNavOpen} navScrolled={navScrolled} />
        <div style={{ textAlign: 'center', padding: '8rem 1.5rem 6rem' }}>
          <h1 style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: '2rem', marginBottom: '1rem' }}>Post not found</h1>
          <p style={{ color: '#5A5650', marginBottom: '1.5rem' }}>The article you're looking for doesn't exist.</p>
          <button onClick={() => navigate('/blog')} style={{ background: '#1B6B4A', color: '#fff', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '0.5rem', fontWeight: 600, cursor: 'pointer' }}>
            Back to blog
          </button>
        </div>
      </div>
    );
  }

  const isDraft = post.status === 'draft';
  const canonicalUrl = `https://letscoparent.us/blog/${post.slug}`;
  const isoDate = formatDateISO(post.date);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
    headline: post.title,
    description: post.metaDescription,
    image: 'https://letscoparent.us/logo512.png',
    datePublished: isoDate,
    dateModified: isoDate,
    author: {
      '@type': 'Organization',
      name: 'coparent',
      url: 'https://letscoparent.us',
    },
    publisher: {
      '@type': 'Organization',
      name: 'coparent',
      logo: {
        '@type': 'ImageObject',
        url: 'https://letscoparent.us/logo512.png',
      },
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://letscoparent.us/' },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://letscoparent.us/blog' },
      { '@type': 'ListItem', position: 3, name: post.title, item: canonicalUrl },
    ],
  };

  return (
    <div className="landing-page blog-post-page">
      <Helmet>
        <title>{`${post.title} | coparent blog`}</title>
        <meta name="description" content={post.metaDescription} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="coparent" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:url" content={canonicalUrl} />
        {isDraft && <meta name="robots" content="noindex, nofollow" />}
        {!isDraft && <script type="application/ld+json">{JSON.stringify(structuredData)}</script>}
        {!isDraft && <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>}
      </Helmet>

      <BlogNav navigate={navigate} mobileNavOpen={mobileNavOpen} setMobileNavOpen={setMobileNavOpen} navScrolled={navScrolled} />

      {isDraft && (
        <div className="blog-draft-banner" role="status">
          <strong>Draft preview</strong> — not visible on /blog. To publish, change <code>status: 'draft'</code> → <code>status: 'published'</code> in <code>packages/web/src/data/blogPosts.js</code>.
        </div>
      )}

      <header className="blog-post-hero">
        <span className="blog-post-label">{post.category}</span>
        <h1>{post.title}</h1>
        <p className="blog-post-meta">
          {post.date}
          <span className="dot">&middot;</span>
          {post.readTime}
        </p>
      </header>

      <article className="blog-article">
        {post.content.map((block, i) => renderBlock(block, i))}
      </article>

      <div className="blog-post-back">
        <Link to="/blog">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M19 12H5" /><path d="m12 19-7-7 7-7" /></svg>
          All articles
        </Link>
      </div>

      <footer className="blog-footer">
        <Link to="/" className="blog-footer-logo">coparent</Link>
        <p>Parenting in sync.</p>
      </footer>
    </div>
  );
};

export default BlogPostPage;
